import { useCallback, useState } from 'react';
import { Form, Formik } from 'formik';
import type { FormikHelpers } from 'formik';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import { EMPTY_OBJECT, EMPTY_STRING } from '@e-socna/empty';

import { useUiContext } from '../../contexts/UiContext/hooks/useUiContext.hook';
import { useDialogAutoFocus } from '../../hooks/useDialogAutoFocus.hook';

import { MemoBeforeSubmitForm } from '../MemoBeforeSubmit';
import { useMemos } from '../MemoBeforeSubmit/hooks/useMemos.hook';
import { AlertMessages } from '../../components/AlertMessages';

import type { SubmitHandler } from './types/submit-handler.type';

interface Props<V> {
  readonly askMemoBeforeSubmit?: boolean;
  readonly cardinality: 'one' | 'all';
  readonly isDialogOpen: boolean;
  readonly onCloseDialog: () => void;
  readonly onSubmit: SubmitHandler<V>;
  readonly useDirectMutation: boolean;
}

export const FlushResolvableServiceUsagesForm = <V extends object>({
  askMemoBeforeSubmit = true,
  cardinality,
  isDialogOpen,
  onCloseDialog: handleCloseDialog,
  onSubmit: pHandleSubmit,
  useDirectMutation,
}: Readonly<Props<V>>) => {
  const { ui } = useUiContext();

  const dialogAutoFocusSlotProps = useDialogAutoFocus();

  // on error
  const [formError, setFormError] = useState<readonly string[] | null>(null);

  const { clearMemos, memos, setMemos } = useMemos();

  const handleSubmit = useCallback(
    (values: V, formikHelpers: FormikHelpers<V>) => {
      void pHandleSubmit({ values, memos, formikHelpers, setFormError })?.then(
        clearMemos,
      );
    },
    [clearMemos, pHandleSubmit, memos],
  );

  const titleMessage =
    cardinality === 'all'
      ? 'Confirmation de la suppression du chiffrage spécifié'
      : 'Confirmation de la suppression de la prestation';

  const confirmationMessage =
    cardinality === 'all'
      ? 'Êtes-vous sûr de vouloir hériter du chiffrage ?'
      : 'Êtes-vous sûr de vouloir hériter de la prestation ?';

  return (
    <Formik
      enableReinitialize
      initialValues={EMPTY_OBJECT as V}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit: formikSubmit, isSubmitting, submitForm, isValid }) => {
        const isLoading = isSubmitting || ui.isBackgroundOperationRunning;

        const submitLabel = 'Hériter';

        return (
          <Form onSubmit={formikSubmit}>
            {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
            {!!askMemoBeforeSubmit && (
              <MemoBeforeSubmitForm
                clearMemos={clearMemos}
                isLoading={isLoading}
                memos={memos}
                onConfirm={submitForm}
                setMemos={setMemos}
                submitLabel={submitLabel}
              />
            )}
            <Dialog
              aria-describedby="flush-resolvable-service-usage-dialog-description"
              aria-labelledby="flush-resolvable-service-usage-dialog-title"
              onClose={handleCloseDialog}
              open={isDialogOpen}
              slotProps={dialogAutoFocusSlotProps}
            >
              <DialogTitle id="flush-resolvable-service-usage-dialog-title">
                {titleMessage}
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="flush-resolvable-service-usage-dialog-description">
                  {confirmationMessage}
                  {useDirectMutation
                    ? ' Cette action est définitive.'
                    : EMPTY_STRING}
                </DialogContentText>
                {!!formError && (
                  <AlertMessages messages={formError} severity="error" />
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDialog} size="large">
                  Annuler
                </Button>
                <Button
                  color="warning"
                  data-dialog-autofocus
                  disabled={!isValid}
                  loading={isLoading}
                  // eslint-disable-next-line react/jsx-no-bind
                  onClick={
                    askMemoBeforeSubmit
                      ? () => {
                          setMemos((oldMemos) => ({
                            ...oldMemos,
                            isOpenMemoDialog: true,
                          }));
                        }
                      : submitForm
                  }
                  size="large"
                  variant="contained"
                >
                  {submitLabel}
                </Button>
              </DialogActions>
            </Dialog>
          </Form>
        );
      }}
    </Formik>
  );
};
